import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Eye, Check, Printer, X, ChevronDown, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TemplateRenderer } from './templates/TemplateRenderer';
import {
  DISENO_INFO, TIPO_LABEL, SAMPLE_DATA, TAMANO_INFO, ORIENTACION_INFO, getDimensiones,
  type TipoDocumento, type DisenoPlantilla, type TamanoHoja, type Orientacion, type TemplateData,
} from './templates/template-types';
import { usePlantillasConfig, useUpdatePlantillaConfig, type PlantillaDocumentoConfig } from './templates/usePlantillaConfig';

const TIPOS:   TipoDocumento[]   = ['receta', 'orden', 'laboratorio', 'presupuesto', 'plan-tratamiento', 'consentimiento'];
const DISENOS: DisenoPlantilla[] = ['clasico', 'minimalista', 'corporativo', 'barra', 'personalizada'];
const TAMANOS: TamanoHoja[]      = ['a4', 'carta', 'oficio', 'a5', 'media-carta'];
const ORIENTACIONES: Orientacion[] = ['vertical', 'horizontal'];

const TIPO_TAB: Record<TipoDocumento, string> = {
  receta:             'Receta',
  orden:              'Orden',
  laboratorio:        'Laboratorio',
  presupuesto:        'Presupuesto',
  'plan-tratamiento': 'Plan de Tto.',
  consentimiento:     'Consentimiento',
};

const MINI_W = 180;

function MiniPreview({ diseno, tamano, orientacion, disenoPersonalizadoHtml, active }: {
  diseno: DisenoPlantilla; tamano: TamanoHoja; orientacion: Orientacion; disenoPersonalizadoHtml?: string | null; active: boolean;
}) {
  const { width, height } = getDimensiones(tamano, orientacion);
  const scale  = MINI_W / width;
  const miniH  = Math.round(MINI_W * height / width);
  return (
    <div style={{ width: MINI_W, height: miniH, overflow: 'hidden', position: 'relative', borderRadius: 8, border: active ? '2px solid #2563eb' : '2px solid #e5e7eb', boxShadow: active ? '0 0 0 3px #2563eb22' : undefined }}>
      <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
        <TemplateRenderer data={SAMPLE_DATA} diseno={diseno} width={width} height={height} disenoPersonalizadoHtml={disenoPersonalizadoHtml} />
      </div>
    </div>
  );
}

export function PlantillasPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TipoDocumento>('receta');
  const [preview,   setPreview]   = useState<DisenoPlantilla | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const { data: selecciones } = usePlantillasConfig();
  const updateMutation = useUpdatePlantillaConfig();

  function update(tipo: TipoDocumento, patch: Partial<PlantillaDocumentoConfig>) {
    if (!selecciones) return;
    const next = { ...selecciones[tipo], ...patch };
    updateMutation.mutate({ tipo, patch: next });
  }

  function handlePrint() {
    const el = printRef.current;
    if (!el || !selecciones) return;
    const { cssSize } = getDimensiones(selecciones[activeTab].tamanoHoja, selecciones[activeTab].orientacion);
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff;font-family:Arial,sans-serif}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Vista previa</title><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => { w.print(); };
  }

  if (!selecciones) return null;

  const cfg = selecciones[activeTab];
  const { width: pw, height: ph } = getDimensiones(cfg.tamanoHoja, cfg.orientacion);
  const sampleData: TemplateData = { ...SAMPLE_DATA, tipo: activeTab };

  const MODAL_SCALE = 0.82;
  const modalW = Math.round(pw * MODAL_SCALE);
  const modalH = Math.round(ph * MODAL_SCALE);

  return (
    <div className="space-y-6">

      {/* Configuración actual — resumen arriba, antes de la elección de plantillas */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Configuración actual</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          {TIPOS.map(t => (
            <div key={t} className="text-xs space-y-0.5">
              <p className="text-text-secondary">{TIPO_LABEL[t]}</p>
              <p className="font-semibold text-text-primary">{DISENO_INFO[selecciones[t].diseno].label}</p>
              <p className="text-[10px] text-text-secondary">
                {TAMANO_INFO[selecciones[t].tamanoHoja].label} · {ORIENTACION_INFO[selecciones[t].orientacion].label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Type tabs + paper size + orientation selectors */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Mobile: select */}
        <div className="sm:hidden relative">
          <select
            value={activeTab}
            onChange={e => setActiveTab(e.target.value as TipoDocumento)}
            className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            {TIPOS.map(t => <option key={t} value={t}>{TIPO_TAB[t]}</option>)}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        {/* Desktop: pill tabs */}
        <div className="hidden sm:flex gap-1 bg-gray-100 p-1 rounded-xl">
          {TIPOS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={cn('px-4 py-1.5 rounded-lg text-xs font-medium transition-all',
                activeTab === t ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
              )}>
              {TIPO_TAB[t]}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">Tamaño de hoja:</span>
            <div className="relative">
              <select
                value={cfg.tamanoHoja}
                onChange={e => update(activeTab, { tamanoHoja: e.target.value as TamanoHoja })}
                className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs font-medium text-text-primary cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
              >
                {TAMANOS.map(t => (
                  <option key={t} value={t}>{TAMANO_INFO[t].label}</option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">Orientación:</span>
            <div className="relative">
              <select
                value={cfg.orientacion}
                onChange={e => update(activeTab, { orientacion: e.target.value as Orientacion })}
                className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs font-medium text-text-primary cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
              >
                {ORIENTACIONES.map(o => (
                  <option key={o} value={o}>{ORIENTACION_INFO[o].label}</option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Design grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {DISENOS.map(d => {
          const isActive = cfg.diseno === d;
          const isPersonalizada = d === 'personalizada';
          return (
            <div key={d} onClick={() => update(activeTab, { diseno: d })}
              className={cn('group cursor-pointer rounded-2xl border-2 p-3 transition-all space-y-3 overflow-hidden',
                isActive ? 'border-primary-500 bg-primary-50/40' : 'border-gray-100 bg-white hover:border-gray-300'
              )}>

              <MiniPreview
                diseno={d}
                tamano={cfg.tamanoHoja}
                orientacion={cfg.orientacion}
                disenoPersonalizadoHtml={isPersonalizada ? cfg.disenoPersonalizadoHtml : undefined}
                active={isActive}
              />

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-text-primary">{DISENO_INFO[d].label}</span>
                  {isActive && (
                    <span className="flex items-center gap-0.5 text-[10px] font-bold text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-full">
                      <Check size={9} /> Activo
                    </span>
                  )}
                </div>
                <p className="text-[10.5px] text-text-secondary leading-snug">{DISENO_INFO[d].descripcion}</p>
                <div className="flex items-center gap-3 mt-1">
                  {isPersonalizada ? (
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/configuracion/plantillas/${activeTab}/personalizar`); }}
                      className="flex items-center gap-1 text-[10.5px] text-primary-600 hover:text-primary-800 transition-colors"
                    >
                      <Pencil size={11} /> Editar en el editor visual
                    </button>
                  ) : (
                    <button
                      onClick={e => { e.stopPropagation(); setPreview(d); }}
                      className="flex items-center gap-1 text-[10.5px] text-primary-600 hover:text-primary-800 transition-colors"
                    >
                      <Eye size={11} /> Vista previa completa
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl">

            <div className="flex items-center justify-between px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold text-text-primary text-sm">{DISENO_INFO[preview].label}</h2>
                <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{TIPO_LABEL[activeTab]}</span>
                <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-100">
                  {TAMANO_INFO[cfg.tamanoHoja].label} · {ORIENTACION_INFO[cfg.orientacion].label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint}
                  className="flex items-center gap-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
                  <Printer size={12} /> Imprimir ejemplo
                </button>
                <button onClick={() => setPreview(null)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Preview scaled to paper size */}
            <div className="p-3 md:p-6 overflow-x-auto">
              <div style={{ width: modalW, height: modalH, overflow: 'hidden', position: 'relative', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: 4, margin: '0 auto' }}>
                <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${MODAL_SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
                  <TemplateRenderer data={sampleData} diseno={preview} width={pw} height={ph} disenoPersonalizadoHtml={cfg.disenoPersonalizadoHtml} />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
              <p className="text-xs text-text-secondary">Vista con datos de ejemplo. El documento real usará los datos del paciente y médico.</p>
              {cfg.diseno !== preview ? (
                <button
                  onClick={() => { update(activeTab, { diseno: preview }); setPreview(null); }}
                  className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors font-medium">
                  Usar este diseño para {TIPO_LABEL[activeTab].toLowerCase()}
                </button>
              ) : (
                <span className="flex items-center gap-1 text-xs text-primary-600 font-medium">
                  <Check size={12} /> Diseño activo para {TIPO_LABEL[activeTab].toLowerCase()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
